import React, { useState } from "react";
import axios from "axios";
import { Button, CircularProgress, Box, Typography, Snackbar, Alert, Grid } from "@mui/material";
import { FaLightbulb, FaCogs, FaProjectDiagram, FaRocket, FaBook, FaIndustry } from "react-icons/fa";
import { LearnersIdeavizeSSO } from "../../helper/base_url";

export default function IdeavizePage() {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSSO = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("company_token");

      const response = await axios.post(
        LearnersIdeavizeSSO,
        { ideavizeUrl: "https://ideavize.huebits.in/login" },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.IdeavizeSsoUrl) {
        window.open(
          response.data.IdeavizeSsoUrl,
          "_blank",
          "noopener,noreferrer"
        );
        setSnackbar({
          open: true,
          message: "Redirecting to Ideavize AI...",
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Failed to get SSO URL. Try again.",
          severity: "error",
        });
      }
    } catch (err) {
      console.error("SSO Error:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Something went wrong.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      title: "Instant Project Ideas",
      description:
        "Generate industry-grade problem statements in seconds across AI, IoT, robotics, cybersecurity, and embedded systems — tailored to your needs.",
      icon: <FaLightbulb size={30} color="rgb(27, 110, 244)" />,
    },
    {
      title: "Deep-Tech Specialization",
      description:
        "Covering Edge AI, Generative AI, IIoT, drones & more — future-ready ideas aligned with industry trends.",
      icon: <FaCogs size={30} color="rgb(27, 110, 244)" />,
    },
    {
      title: "Structured Implementation Plans",
      description:
        "Each idea includes step-by-step roadmap, architecture, tools, and strategy to reduce time-to-build.",
      icon: <FaProjectDiagram size={30} color="rgb(27, 110, 244)" />,
    },
    {
      title: "Smart Resources & Learning",
      description:
        "Auto-matched datasets, frameworks, components, and curated learning — accelerate your development.",
      icon: <FaBook size={30} color="rgb(27, 110, 244)" />,
    },
    {
      title: "Industry & Academia Ready",
      description:
        "Proposals meet academic rigor & industry expectations — ideal for innovation cells, grants & pitching.",
      icon: <FaIndustry size={30} color="rgb(27, 110, 244)" />,
    },
    {
      title: "Domain-to-Deployment Coverage",
      description:
        "Tackle real-world challenges in healthcare, agriculture, defense, and more with the right tech stack.",
      icon: <FaRocket size={30} color="rgb(27, 110, 244)" />,
    },
  ];

  return (
    <Box
          sx={{
            inlineSize: '100%',
            backgroundColor: '#f4f6f8',
            padding: "80px 20px 20px 20px",
            blockSize: '100%',
            overflowY: 'auto',
            position: 'relative',
          }}
        >
      {/* Hero Section */}
      <Box
        sx={{
          textAlign: "center",
          py: { xs: 4, md: 6 },
          mb: 6,
          borderRadius: 3,
          background: "linear-gradient(135deg, #043885, #0077a3)",
          color: "white",
        }}
      >
        <Typography
          variant="h3"
          fontWeight="800"
          sx={{
            mb: 2,
            fontSize: { xs: "2rem", md: "2.6rem" },
          }}
        >
          Ideavize AI – Your AI-Powered Gateway to Innovation
        </Typography>

        <Typography
          variant="body1"
          sx={{
            mb: 4,
            maxWidth : 700,
            mx: "auto",
            lineHeight : 1.6,
            color: "rgba(255,255,255,0.85)",
          }}
        >
          Transform your vision into reality with AI-generated project ideas across AI, ML, DL, Generative AI,
          IoT, Edge AI, Cloud Computing, Data Science, Embedded Systems, and Full-Stack Development.
          Get structured implementation plans, curated datasets, expert tools, and mentorship — empowering students, 
          researchers, professionals, startups, and MSMEs to innovate faster and achieve real-world impact.
        </Typography>

        <Button
          variant="contained"
          onClick={handleSSO}
          disabled={loading}
          sx={{
            borderRadius: 3,
            px: 5,
            py: 1.5,
            fontWeight: "bold",
            textTransform: "none",
            fontSize: "1rem",
            background: "linear-gradient(90deg, rgb(27, 110, 244), rgba(0, 98, 255, 1))",
            "&:hover": {
              background: "linear-gradient(90deg, rgb(27, 110, 244), rgba(0, 97, 254, 1))",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
          ) : (
            "Get Started"
          )}
        </Button>
      </Box>

      {/* Features Section */}
      <Typography
        variant="h4"
        fontWeight="700"
        sx={{ mb: 4, textAlign: "center", color: "#043885" }}
      >
        Your Deep-Tech Innovation Toolkit
      </Typography>
      <Typography variant="body1" sx={{ mb: 4, textAlign: "center", color: "#555" }}>
        Everything you need to transform bold ideas into real-world impact.
      </Typography>

      <Grid container spacing={6}>
        {features.map((item, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            key={index}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              padding: 2,
              transition: "all 0.3s ease",
              cursor: "pointer",
              "&:hover": {
                transform: "translateY(-8px)",
                "& .feature-title": { color: "#1b6ef4" },
                "& .feature-desc": { color: "#000" },
              },
            }}
          >
            {/* Icon Circle */}
            <Box
              sx={{
                minWidth : 60,
                minHeight : 60,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #e0e7ff, #c7d2fe)",
                color: "#1b6ef4",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 28,
                flexShrink: 0,
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              }}
            >
              {item.icon}
            </Box>

            {/* Text */}
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                className="feature-title"
                sx={{ mb: 1, color: "#043885", transition: "color 0.3s ease" }}
              >
                {item.title}
              </Typography>
              <Typography
                variant="body2"
                className="feature-desc"
                sx={{ color: "#555", transition: "color 0.3s ease", lineHeight : 1.6 }}
              >
                {item.description}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
